import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { MaskitoOptions } from '@maskito/core';
import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { IUser, User } from '@ms/core/model/user.model';
import { EventManager } from '@ms/core/service/event-manager.service';
import { fake_school_id } from '@ms/core/util/constant-util';
import { dateRange } from '@ms/core/util/date-util';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { IUserProfile } from 'app/layout/model/profile.model';
import { Language } from 'app/main/shared/model/translateble-string.model';
import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';

export interface SessionState {
    authenticated: boolean;
    eventFire: boolean;
    user: User | null;
}

export type CurrencyFormatType = {
    thousandsSeparator: string;
    padFractionalZeros: boolean;
    decimalSeparator: string;
    scale: number;
};
export type DateFormatType = {
    pattern: string;
    formatDate: string;
    short: string;
    medium: string;
    shortDate: string;
    mediumDate: string;
    monthAndYearMask: string;
    longDate: string;
    fullDate: string;
    shortTime: string;
    mediumTime: string;
    maskito: Required<MaskitoOptions>;
    range: any;
    min: Date;
    max: Date;
};

export type CountryFormatType = {
    currency: CurrencyFormatType;
    decimal: CurrencyFormatType;
    date: DateFormatType;
};

const DECIMAL_COMMA_DOT: CurrencyFormatType = {
    thousandsSeparator: ',',
    padFractionalZeros: true,
    decimalSeparator: '.',
    scale: 2,
};

const DECIMAL_DOT_COMMA: CurrencyFormatType = {
    thousandsSeparator: '.',
    padFractionalZeros: true,
    decimalSeparator: ',',
    scale: 2,
};

const DECIMAL_SPACE_COMMA: CurrencyFormatType = {
    thousandsSeparator: ' ',
    padFractionalZeros: true,
    decimalSeparator: ',',
    scale: 2,
};

const DECIMAL_SPACE_DOT: CurrencyFormatType = {
    thousandsSeparator: ' ',
    padFractionalZeros: true,
    decimalSeparator: '.',
    scale: 2,
};

const CURRENCY_DOT_COMMA: CurrencyFormatType = {
    thousandsSeparator: '.',
    padFractionalZeros: true,
    decimalSeparator: ',',
    scale: 2,
};

const CURRENCY_SPACE_COMMA: CurrencyFormatType = {
    thousandsSeparator: ' ',
    padFractionalZeros: true,
    decimalSeparator: ',',
    scale: 2,
};

const CURRENCY_SPACE_DOT: CurrencyFormatType = {
    thousandsSeparator: ' ',
    padFractionalZeros: true,
    decimalSeparator: '.',
    scale: 2,
};
const CURRENCY_COMMA_DOT: CurrencyFormatType = {
    thousandsSeparator: ',',
    padFractionalZeros: true,
    decimalSeparator: '.',
    scale: 2,
};

/*
const DATE_MONTH_DAY_YEAR_WITH_SLASH: DateFormatType = {
    pattern: 'MM/DD/YYYY',
    formatDate: 'MM/dd/yyyy',
    short: 'M/d/yy, HH:mm',
    medium: 'MMM d, y, HH:mm',
    shortDate: 'M/d/yy',
    mediumDate: 'MMM d, y',
    longDate: 'MMMM d, y',
    fullDate: 'EEEE, MMMM d, y',
    shortTime: 'HH:mm',
    mediumTime: 'HH:mm:ss',
    maskito: maskitoDateOptionsGenerator({ mode: 'mm/dd/yyyy', separator: '/' }),
    range: dateRange.en,
    min: new Date(1930, 0, 1),
    max: new Date(2999, 0, 1),
};
*/

const DATE_MONTH_DAY_YEAR_WITH_SLASH_USA: DateFormatType = {
    pattern: 'MM/DD/YYYY',
    formatDate: 'MM/dd/yyyy',
    short: 'M/d/yy, H:mm a',
    medium: 'MMM d, y, h:mm a',
    shortDate: 'M/d/yy',
    monthAndYearMask: 'MM/yy',
    mediumDate: 'MMM d, y',
    longDate: 'MMMM d, y',
    fullDate: 'EEEE, MMMM d, y',
    shortTime: 'h:mm a',
    mediumTime: 'h:mm:ss a',
    maskito: maskitoDateOptionsGenerator({ mode: 'mm/dd/yyyy', separator: '/' }),
    range: dateRange.en,
    min: new Date(1930, 0, 1),
    max: new Date(2999, 0, 1),
};

const DATE_DAY_MONTH_YEAR_WITH_SLASH: DateFormatType = {
    pattern: 'DD/MM/YYYY',
    formatDate: 'dd/MM/yyyy',
    short: 'd/M/yy, HH:mm',
    medium: 'd MMM, y, HH:mm',
    monthAndYearMask: 'MM/yy',
    shortDate: 'M/d/yy',
    mediumDate: 'MMM d, y',
    longDate: 'MMMM d, y',
    fullDate: 'EEEE, MMMM d, y',
    shortTime: 'HH:mm',
    mediumTime: 'HH:mm:ss',
    maskito: maskitoDateOptionsGenerator({ mode: 'dd/mm/yyyy', separator: '/' }),
    range: dateRange.en,
    min: new Date(1930, 0, 1),
    max: new Date(2999, 0, 1),
};

const DATE_DAY_MONTH_YEAR_WITH_DOT: DateFormatType = {
    pattern: 'DD.MM.YYYY',
    formatDate: 'dd.MM.yyyy',
    short: 'd.M.yy, HH:mm',
    medium: 'd MMM, y, HH:mm',
    shortDate: 'M.d.yy',
    monthAndYearMask: 'MM.yy',
    mediumDate: 'MMM d, y',
    longDate: 'MMMM d, y',
    fullDate: 'EEEE, MMMM d, y',
    shortTime: 'HH:mm',
    mediumTime: 'HH:mm:ss',
    range: dateRange.kk,
    maskito: maskitoDateOptionsGenerator({ mode: 'dd/mm/yyyy', separator: '.' }),
    min: new Date(1930, 0, 1),
    max: new Date(2999, 0, 1),
};
// Country formats (Currency and Date)
const FORMAT_RUS: CountryFormatType = {
    currency: CURRENCY_SPACE_COMMA,
    decimal: DECIMAL_DOT_COMMA,
    date: DATE_DAY_MONTH_YEAR_WITH_DOT,
};

const FORMAT_EUROPE: CountryFormatType = {
    currency: CURRENCY_DOT_COMMA,
    decimal: DECIMAL_DOT_COMMA,
    date: DATE_DAY_MONTH_YEAR_WITH_DOT,
};

const FORMAT_EK: CountryFormatType = {
    currency: CURRENCY_COMMA_DOT,
    decimal: DECIMAL_DOT_COMMA,
    date: DATE_DAY_MONTH_YEAR_WITH_SLASH,
};

const FORMAT_FRA: CountryFormatType = {
    currency: CURRENCY_DOT_COMMA,
    decimal: DECIMAL_DOT_COMMA,
    date: DATE_DAY_MONTH_YEAR_WITH_SLASH,
};

const FORMAT_USA: CountryFormatType = {
    currency: CURRENCY_COMMA_DOT,
    decimal: DECIMAL_COMMA_DOT,
    date: DATE_MONTH_DAY_YEAR_WITH_SLASH_USA,
};

export const PROFILES_FORMAT: { [key: string]: CountryFormatType } = {
    KAZ: FORMAT_RUS,
    RUS: FORMAT_RUS,
    USA: FORMAT_USA,
    GBR: FORMAT_EK,
    DEU: FORMAT_EUROPE,
    TRY: FORMAT_EUROPE,
    FRA: FORMAT_FRA,
};

export const SchoolDecimalFormat = [
    { key: 'COMMA_DOT', value: '1,234.25', formatter: DECIMAL_COMMA_DOT },
    { key: 'DOT_COMMA', value: '1.234,25', formatter: DECIMAL_DOT_COMMA },
    { key: 'SPACE_COMMA', value: '1 234,25', formatter: DECIMAL_SPACE_COMMA },
    { key: 'SPACE_DOT', value: '1 234.25', formatter: DECIMAL_SPACE_DOT },
];

export const SchoolCurrencyFormat = [
    { key: 'COMMA_DOT', value: '1,234.25', formatter: CURRENCY_COMMA_DOT },
    { key: 'DOT_COMMA', value: '1.234,25', formatter: CURRENCY_DOT_COMMA },
    { key: 'SPACE_COMMA', value: '1 234,25', formatter: CURRENCY_SPACE_COMMA },
    { key: 'SPACE_DOT', value: '1 234.25', formatter: CURRENCY_SPACE_DOT },
];

export const SchoolDateFormat = [
    { key: 'MONTH_DAY_YEAR_WITH_SLASH', value: 'MM/DD/YYYY', formatter: DATE_MONTH_DAY_YEAR_WITH_SLASH_USA },
    { key: 'DAY_MONTH_YEAR_WITH_SLASH', value: 'DD/MM/YYYY', formatter: DATE_DAY_MONTH_YEAR_WITH_SLASH },
    { key: 'DAY_MONTH_YEAR_WITH_DOT', value: 'DD.MM.YYYY', formatter: DATE_DAY_MONTH_YEAR_WITH_DOT },
];

export const PROFILES: { [key: string]: IUserProfile } = {
    'en-us': {
        id: 'en-us',
        title: 'English(US)',
        flag: 'us',
        code: 'en-US',
        locale: 'en-us',
        format: PROFILES_FORMAT.USA,
    },
    en: {
        id: 'en',
        title: 'English',
        flag: 'en',
        code: 'en-US',
        locale: 'en',
        format: PROFILES_FORMAT.USA, // Default
    },
    de: {
        id: 'de',
        title: 'Deutsch',
        flag: 'de',
        code: 'de-DE',
        locale: 'de',
        format: PROFILES_FORMAT.DEU, // Default
    },
    fr: {
        id: 'fr',
        title: 'Français',
        flag: 'fr',
        code: 'fr-FR',
        locale: 'fr',
        format: PROFILES_FORMAT.FRA, // Default
    },
    kk: {
        id: 'kk',
        title: 'Қазақша',
        flag: 'kk',
        code: 'kk-KZ',
        locale: 'kk',
        format: PROFILES_FORMAT.KAZ, // Default
    },
    ru: {
        id: 'ru',
        title: 'Русский',
        flag: 'ru',
        code: 'ru-RU',
        locale: 'ru',
        format: PROFILES_FORMAT.RUS, // Default
    },
    tr: {
        id: 'tr',
        title: 'Türkçe',
        flag: 'tr',
        code: 'tr-TR',
        locale: 'tr',
        format: PROFILES_FORMAT.TRY, // Default
    },
};

// eslint-disable-next-line func-style
export function createInitialSessionState(): SessionState {
    console.log('SESSION-INIT');
    const user: IUser = new User();
    user.schools = [];
    user.activeSchool = {
        id: fake_school_id,
        name: 'NOT-ASSIGNED',
        shortName: 'NOT-ASSIGNED',
        language: Language.en,
        active: true,
    };
    return {
        authenticated: false,
        eventFire: false,
        user,
    };
}

const SESSION_STATE = new InjectionToken<SessionState>('SessionState', {
    factory: () => createInitialSessionState(),
});

export const SessionStore = signalStore(
    { providedIn: 'root' },
    withState(() => inject(SESSION_STATE)),
    withDevtools('campus-session'),
    withMethods(store => ({
        setSession(session: SessionState): void {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            patchState(store, _state => ({ ...session }));
        },

        clear(): void {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            patchState(store, _state => createInitialSessionState());
        },
        get(): SessionState {
            return {
                authenticated: store.authenticated(),
                user: structuredClone(store.user()),
                eventFire: store.eventFire(),
            };
        },
    }))
);

@Injectable({ providedIn: 'root' })
export class SessionStoreService {
    eventManager = inject(EventManager);
    cookieService = inject(CookieService);
    store = inject(SessionStore);

    set(authenticated: boolean, user: User, eventFire: boolean) {
        this.store.setSession({ user: { ...user }, authenticated, eventFire });
        if (eventFire) {
            this.changeEvent({
                authenticated,
                user,
                eventFire,
            });
        }
        // Active school set
        if (authenticated && user?.activeSchool) {
            this.cookieService.set(`${user.id}_active_school`, user.activeSchool.id);
        }
    }

    changeEvent(state: SessionState) {
        this.eventManager.broadcast({
            name: 'user-changed',
            state,
        });
    }

    get user(): User {
        const result = this.store.get().user;
        return result?.id ? { ...result } : undefined;
    }

    get isAuthenticated(): boolean {
        const result = this.store.get();
        return result?.user?.id && result?.authenticated;
    }

    get profile(): IUserProfile {
        const user = this.store.get().user;
        const profile = { ...PROFILES[user.langKey] };
        profile.locale = user.locale;
        if (user.activeSchool?.decimalFormat) {
            const decimalFormat = SchoolDecimalFormat.find(f => f.key == user.activeSchool.decimalFormat);
            profile.format = { ...profile.format, decimal: decimalFormat.formatter };
            const currencyFormat = SchoolCurrencyFormat.find(f => f.key == user.activeSchool.currencyFormat);
            profile.format = { ...profile.format, currency: currencyFormat.formatter };
            const dateFormat = SchoolDateFormat.find(f => f.key == user.activeSchool.dateFormat);
            profile.format = { ...profile.format, date: dateFormat.formatter };
        } else {
            // School Info was empty for old records
            if (user.activeSchool?.address?.country) {
                const format = PROFILES_FORMAT[user.activeSchool.address.country.code];
                if (format) {
                    profile.format = { ...format };
                } else {
                    profile.format = { ...PROFILES_FORMAT.USA };
                }
            }
        }
        return profile;
    }

    get state(): SessionState {
        const result = this.store.get();
        return result ? { ...result } : undefined;
    }

    clear() {
        this.store.clear();
        this.changeEvent(createInitialSessionState());
        if (environment.production) {
            // this.cookieService.deleteAll('/');
        }
    }
}
